import { AlertTriangle, Clock, Droplets, ListChecks } from "lucide-react";
import { Metric } from "../Metric";
import { Badge } from "../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { formatDateRange, formatDateTime } from "../../../lib/baseline/format";
import { categoryTone } from "../../../lib/events";
import type { UploadSummary } from "../../../types/domain";

export function SummaryView({ summary }: { summary: UploadSummary }) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric icon={<ListChecks className="h-4 w-4" />} label="Total events" value={summary.totalEvents.toLocaleString()} />
        <Metric icon={<Clock className="h-4 w-4" />} label="Date range" value={<span className="text-lg">{formatDateRange(summary.dateRangeStart, summary.dateRangeEnd)}</span>} />
        <Metric icon={<AlertTriangle className="h-4 w-4" />} label="Critical issues" value={summary.criticalIssues} tone="critical" />
        <Metric icon={<Droplets className="h-4 w-4" />} label="Warnings" value={summary.warnings} tone="warning" />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Most active categories</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {summary.mostActiveCategories.map((item) => (
            <Badge key={item.category} className={categoryTone(item.category)}>
              {item.category.replace("_", " ")} · {item.count}
            </Badge>
          ))}
        </CardContent>
      </Card>
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Programs seen</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {summary.programsSeen.length ? summary.programsSeen.map((program) => <Badge key={program} className="bg-indigo-50 text-indigo-800 ring-indigo-200">Program {program}</Badge>) : <p className="text-sm text-slate-600">No program values found.</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Zones seen</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {summary.zonesSeen.length ? summary.zonesSeen.map((zone) => <Badge key={zone} className="bg-emerald-50 text-emerald-800 ring-emerald-200">Zone {zone}</Badge>) : <p className="text-sm text-slate-600">No zone values found.</p>}
          </CardContent>
        </Card>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>First event</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-semibold text-navy-900">{summary.firstEvent?.title}</p>
            <p className="mt-1 text-sm text-slate-600">{formatDateTime(summary.firstEvent?.timestamp)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Last event</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-semibold text-navy-900">{summary.lastEvent?.title}</p>
            <p className="mt-1 text-sm text-slate-600">{formatDateTime(summary.lastEvent?.timestamp)}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
